// SNIGJ - Dashboard Layout Component - Responsive & Fixed User Panel
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useAppearance } from '../../context/AppearanceContext';
import NotificationCenter from '../NotificationCenter';
import { Button } from '../ui/button';
import { t } from '../../lib/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  FolderOpen, 
  CreditCard,
  Shield,
  Scale,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronDown,
  ChevronRight,
  Bell,
  Search,
  Globe,
  Globe2,
  UserCircle,
  FileBarChart,
  AlertTriangle,
  DollarSign,
  Languages,
  Palette,
  Wallet,
  BarChart3,
  Building2,
  Gavel,
  Database,
  FileSignature,
  Briefcase,
  ClipboardList,
  Calculator,
  BookOpen,
  MapPin,
  Lock,
  Phone,
  Mail,
  Eye,
  HandMetal,
  Activity
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, logout, language, updateLanguage } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { settings: appearanceSettings } = useAppearance();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Track expanded categories
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const saved = localStorage.getItem('snigj_menu_expanded');
    return saved ? JSON.parse(saved) : { justice: true, admin: false };
  });

  const toggleCategory = (category) => {
    const newState = { ...expandedCategories, [category]: !expandedCategories[category] };
    setExpandedCategories(newState);
    localStorage.setItem('snigj_menu_expanded', JSON.stringify(newState));
  };

  // Get custom colors from appearance settings
  const sidebarBg = React.useMemo(() => appearanceSettings?.secondaryColor || '#0f172a', [appearanceSettings?.secondaryColor]);
  const activeColor = React.useMemo(() => appearanceSettings?.primaryColor || '#eab308', [appearanceSettings?.primaryColor]);
  const accentColor = React.useMemo(() => appearanceSettings?.accentColor || '#d4af37', [appearanceSettings?.accentColor]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Judicial roles check
  const judicialRoles = [
    'OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL',
    'GREFFIER_PAIX', 'JUGE_PAIX', 'PRESIDENT_TRIBUNAL_PAIX',
    'GREFFIER_GI', 'SUBSTITUT_PROCUREUR', 'PROCUREUR_REPUBLIQUE',
    'GREFFIER_APPEL', 'CONSEILLER_APPEL', 'PRESIDENT_COUR_APPEL',
    'AVOCAT_GENERAL', 'PROCUREUR_GENERAL',
    'CONSEILLER_CASSATION', 'PRESIDENT_CASSATION',
    'CONSEILLER_ETAT', 'PRESIDENT_CONSEIL_ETAT',
    'JUGE_SUPREME', 'PRESIDENT_COUR_SUPREME',
    'MAGISTRATE', 'JUDICIAL_POLICE_OFFICER',
    'NATIONAL_ADMIN', 'TECH_SUPPORT', 'MINISTER', 'MINISTRE_JUSTICE', 'PRESIDENT', 'PREMIER_MINISTRE', 'SUPER_ADMIN'
  ];
  const isJudicial = judicialRoles.includes(user?.role);
  const isAdmin = ['NATIONAL_ADMIN', 'TECH_SUPPORT', 'MINISTER', 'MINISTRE_JUSTICE', 'PRESIDENT', 'PREMIER_MINISTRE', 'SUPER_ADMIN'].includes(user?.role);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  // Module access check: if user has module_access configured, filter strictly
  const userModules = user?.module_access || [];
  const hasModuleConfig = userModules.length > 0;
  const hasAccess = (moduleCode) => {
    if (isSuperAdmin || isAdmin) return true; // Admins always have full access
    if (!hasModuleConfig) return true; // If no config yet, show based on role (backward compat)
    return userModules.includes(moduleCode);
  };

  // Police-only roles (restricted menu: no Accueil, only Tableau de bord + Interpellations + Autres)
  const policeOnlyRoles = ['AGENT_POLICE', 'POLICE_OFFICER'];
  const isPoliceOnly = policeOnlyRoles.includes(user?.role);

  // Build categorized menu
  const menuCategories = [];

  // === PRINCIPAL (always visible) ===
  const principalItems = [];
  
  // Citizens only see their dashboard (not the admin home page)
  if (user?.role === 'CITIZEN') {
    principalItems.push({ path: '/dashboard', icon: LayoutDashboard, label: language === 'fr' ? 'Accueil' : 'Home' });
    principalItems.push({ path: '/my-profile', icon: UserCircle, label: language === 'fr' ? 'Mon Profil' : 'My Profile' });
    principalItems.push({ path: '/citizen-portal', icon: FileText, label: language === 'fr' ? 'Portail Citoyen' : 'Citizen Portal' });
    principalItems.push({ path: '/citizen/submissions', icon: ClipboardList, label: language === 'fr' ? 'Mes Démarches' : 'My Submissions' });
  } else if (isPoliceOnly) {
    // Police officers: only Tableau de bord (no Accueil)
    principalItems.push({ path: '/dashboard', icon: FileBarChart, label: t(language, 'nav.dashboard') });
  } else {
    // All other non-citizen roles see both home and dashboard
    principalItems.push({ path: '/home', icon: LayoutDashboard, label: language === 'fr' ? 'Accueil' : 'Home' });
    principalItems.push({ path: '/dashboard', icon: FileBarChart, label: t(language, 'nav.dashboard') });
  }

  // Lawyer
  if (user?.role === 'LAWYER') {
    principalItems.push({ path: '/lawyer', icon: Scale, label: language === 'fr' ? 'Tableau Avocat' : 'Lawyer Dashboard' });
  }

  // Border Police
  if (user?.role === 'BORDER_CONTROL_OFFICER') {
    principalItems.push({ path: '/border-police', icon: Shield, label: language === 'fr' ? 'Interface Police' : 'Police Interface' });
  }

  // Police/OPJ Work page
  const policeRoles = ['AGENT_POLICE', 'POLICE_OFFICER', 'OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL', 'COMMISSAIRE_DIVISIONNAIRE', 'COMMISSAIRE_GENERAL', 'PROCUREUR_REPUBLIQUE', 'SUBSTITUT_PROCUREUR', 'PROCUREUR_GENERAL', 'SUPER_ADMIN', 'NATIONAL_ADMIN'];
  if (policeRoles.includes(user?.role)) {
    principalItems.push({ path: '/police-work', icon: HandMetal, label: language === 'fr' ? 'Interpellations / GAV' : 'Apprehensions / Custody' });
  }

  menuCategories.push({
    id: 'principal',
    label: null, // No header for principal items
    icon: null,
    items: principalItems,
    collapsible: false
  });

  // === JUSTICE CIVILE ===
  if (!isPoliceOnly && user?.role !== 'CITIZEN' && user?.role !== 'BORDER_CONTROL_OFFICER' && hasAccess('justice_civile')) {
    const civilJusticeItems = [
      { path: '/cases?type=civil', icon: FileText, label: language === 'fr' ? 'Dossiers Civils' : 'Civil Cases' },
      { path: '/trials?type=civil', icon: Gavel, label: language === 'fr' ? 'Procès Civils' : 'Civil Trials' },
      { path: '/complaints', icon: ClipboardList, label: language === 'fr' ? 'Plaintes' : 'Complaints' },
    ];

    if (isJudicial) {
      civilJusticeItems.push({ path: '/legal-documents', icon: FileSignature, label: language === 'fr' ? 'Docs Juridiques' : 'Legal Docs' });
      civilJusticeItems.push({ path: '/requisitions', icon: Scale, label: language === 'fr' ? 'Réquisitions' : 'Requisitions' });
    }

    menuCategories.push({
      id: 'civil-justice',
      label: language === 'fr' ? 'Justice Civile' : 'Civil Justice',
      icon: Scale,
      items: civilJusticeItems,
      collapsible: true
    });
  }

  // === JUSTICE MILITAIRE ===
  if (!isPoliceOnly && user?.role !== 'CITIZEN' && user?.role !== 'BORDER_CONTROL_OFFICER' && hasAccess('justice_militaire')) {
    const militaryJusticeItems = [
      { path: '/cases?type=military', icon: FileText, label: language === 'fr' ? 'Dossiers Militaires' : 'Military Cases' },
      { path: '/trials?type=military', icon: Gavel, label: language === 'fr' ? 'Procès Militaires' : 'Military Trials' },
    ];

    menuCategories.push({
      id: 'military-justice',
      label: language === 'fr' ? 'Justice Militaire' : 'Military Justice',
      icon: Shield,
      items: militaryJusticeItems,
      collapsible: true
    });
  }

  // === PRISONS (Établissements pénitentiaires) ===
  if (!isPoliceOnly && user?.role !== 'CITIZEN' && user?.role !== 'BORDER_CONTROL_OFFICER' && hasAccess('prisons')) {
    const prisonItems = [
      { path: '/prisons', icon: Building2, label: language === 'fr' ? 'Établissements' : 'Establishments' },
      { path: '/prisoners', icon: Users, label: language === 'fr' ? 'Liste Détenus' : 'Inmates List' },
    ];
    
    if (isJudicial || isAdmin) {
      prisonItems.push({ path: '/prisoners/releases', icon: Lock, label: language === 'fr' ? 'Libérations' : 'Releases' });
    }

    menuCategories.push({
      id: 'prisons',
      label: language === 'fr' ? 'Prisons' : 'Prisons',
      icon: Lock,
      items: prisonItems,
      collapsible: true
    });
  }

  // === DOCUMENTS ===
  // === SUPERVISION HIERARCHIQUE ===
  const supervisionRoles = ['PROCUREUR_GENERAL', 'PROCUREUR_REPUBLIQUE', 'SUBSTITUT_PROCUREUR', 'COMMISSAIRE_PRINCIPAL', 'SUPER_ADMIN', 'NATIONAL_ADMIN', 'TECH_SUPPORT'];
  if (supervisionRoles.includes(user?.role)) {
    menuCategories.push({
      id: 'supervision',
      label: language === 'fr' ? 'Supervision' : 'Supervision',
      icon: Eye,
      items: [
        { path: '/supervision', icon: Eye, label: language === 'fr' ? 'Tableau de bord' : 'Dashboard' },
      ],
      collapsible: true
    });
  }

  // === DOCUMENTS (continued) ===
  if (!isPoliceOnly && user?.role !== 'CITIZEN' && user?.role !== 'BORDER_CONTROL_OFFICER' && hasAccess('documents')) {
    menuCategories.push({
      id: 'documents',
      label: language === 'fr' ? 'Documents' : 'Documents',
      icon: FolderOpen,
      items: [
        { path: '/documents', icon: FolderOpen, label: language === 'fr' ? 'Tous Documents' : 'All Documents' },
        { path: '/legal-library', icon: BookOpen, label: language === 'fr' ? 'Bibliothèque' : 'Library' },
      ],
      collapsible: true
    });
  }

  // === FINANCES (Caisse, Paiements) ===
  if (!isPoliceOnly && user?.role !== 'CITIZEN' && user?.role !== 'BORDER_CONTROL_OFFICER' && hasAccess('finances')) {
    const financeItems = [
      { path: '/cash-register', icon: Calculator, label: language === 'fr' ? 'Caisse' : 'Cash Register' },
      { path: '/payments', icon: CreditCard, label: language === 'fr' ? 'Paiements' : 'Payments' },
    ];
    
    if (isAdmin) {
      financeItems.push({ path: '/admin/cash-overview', icon: BarChart3, label: language === 'fr' ? 'Vue Caisses' : 'Cash Overview' });
      financeItems.push({ path: '/admin/currency', icon: DollarSign, label: language === 'fr' ? 'Taux de change' : 'Exchange Rates' });
    }

    if (['NATIONAL_ADMIN', 'TECH_SUPPORT', 'FINANCIAL_AUDITOR', 'MINISTER', 'PRESIDENT', 'SUPER_ADMIN', 'CLERK'].includes(user?.role)) {
      financeItems.push({ path: '/admin/accounting', icon: DollarSign, label: language === 'fr' ? 'Comptabilité' : 'Accounting' });
    }

    menuCategories.push({
      id: 'finances',
      label: language === 'fr' ? 'Finances' : 'Finances',
      icon: Wallet,
      items: financeItems,
      collapsible: true
    });
  }

  // === RAPPORTS & STATISTIQUES ===
  if (!isPoliceOnly && (isJudicial || isAdmin) && hasAccess('statistiques')) {
    const reportItems = [];
    
    if (isJudicial) {
      reportItems.push({ path: '/reports', icon: BarChart3, label: language === 'fr' ? 'Rapports' : 'Reports' });
    }
    
    if (['NATIONAL_ADMIN', 'TECH_SUPPORT', 'MINISTER', 'PRESIDENT', 'SUPER_ADMIN', 'FINANCIAL_AUDITOR'].includes(user?.role)) {
      reportItems.push({ path: '/statistics', icon: FileBarChart, label: language === 'fr' ? 'Statistiques BI' : 'BI Statistics' });
      reportItems.push({ path: '/analytics', icon: Activity, label: language === 'fr' ? 'Analytique' : 'Analytics' });
    }
    
    if (['NATIONAL_ADMIN', 'TECH_SUPPORT', 'FINANCIAL_AUDITOR', 'MINISTER', 'PRESIDENT', 'SUPER_ADMIN'].includes(user?.role)) {
      reportItems.push({ path: '/audit-logs', icon: FileBarChart, label: language === 'fr' ? 'Journaux' : 'Audit Logs' });
    }

    if (reportItems.length > 0) {
      menuCategories.push({
        id: 'reports',
        label: language === 'fr' ? 'Rapports' : 'Reports',
        icon: BarChart3,
        items: reportItems,
        collapsible: true
      });
    }
  }

  // === ADMINISTRATION ===
  if (isAdmin) {
    const adminItems = [
      { path: '/admin', icon: Shield, label: language === 'fr' ? 'Administration' : 'Admin' },
      { path: '/admin/users', icon: Users, label: language === 'fr' ? 'Utilisateurs' : 'Users' },
      { path: '/admin/structures', icon: Building2, label: language === 'fr' ? 'Structures' : 'Structures' },
      { path: '/admin/offenses', icon: Gavel, label: language === 'fr' ? 'Infractions' : 'Offenses' },
      { path: '/admin/branding', icon: Palette, label: language === 'fr' ? 'Personnalisation' : 'Branding' },
      { path: '/admin/widgets', icon: LayoutDashboard, label: language === 'fr' ? 'Widgets' : 'Widgets' },
      { path: '/admin/document-templates', icon: FileText, label: language === 'fr' ? 'Modèles docs' : 'Templates' },
      { path: '/admin/translations', icon: Languages, label: language === 'fr' ? 'Traductions' : 'Translations' },
      { path: '/admin/config', icon: Settings, label: language === 'fr' ? 'Configuration' : 'Config' },
    ];

    if (isSuperAdmin) {
      adminItems.push({ path: '/admin/data-management', icon: Database, label: language === 'fr' ? 'Gestion Données' : 'Data Management' });
    }

    // SEO item for admins
    adminItems.push({ path: '/admin/seo', icon: Globe2, label: 'SEO & Open Graph' });
    
    // System Config for admins
    adminItems.push({ path: '/admin/system-config', icon: Settings, label: language === 'fr' ? 'Configuration Système' : 'System Config' });
    
    // SMTP Config for super admins only
    if (isSuperAdmin) {
      adminItems.push({ path: '/admin/smtp-config', icon: Mail, label: language === 'fr' ? 'Config. Email (SMTP)' : 'Email Config (SMTP)' });
      adminItems.push({ path: '/admin/security', icon: Lock, label: language === 'fr' ? 'Sécurité (Verrouillage)' : 'Security (Lockout)' });
      adminItems.push({ path: '/admin/payment-config', icon: CreditCard, label: language === 'fr' ? 'Config. Paiements' : 'Payment Config' });
      adminItems.push({ path: '/admin/email-history', icon: Mail, label: language === 'fr' ? 'Historique Emails' : 'Email History' });
    }

    menuCategories.push({
      id: 'admin',
      label: language === 'fr' ? 'Administration' : 'Administration',
      icon: Shield,
      items: adminItems,
      collapsible: true
    });
  }

  // === AUTRES (Carte, Paramètres) ===
  const otherItems = [
    { path: '/map', icon: Globe, label: language === 'fr' ? 'Carte Judiciaire' : 'Judicial Map' },
  ];

  // Border control only for authorized roles (not citizens)
  if (user?.role !== 'CITIZEN') {
    otherItems.push({ path: '/border-control', icon: AlertTriangle, label: language === 'fr' ? 'Contrôle Frontières' : 'Border Control' });
  }

  if (user?.role === 'PRESIDENT') {
    otherItems.push({ path: '/clemency', icon: Scale, label: language === 'fr' ? 'Grâces' : 'Clemency' });
  }

  otherItems.push({ path: '/settings', icon: Settings, label: language === 'fr' ? 'Paramètres' : 'Settings' });

  menuCategories.push({
    id: 'other',
    label: language === 'fr' ? 'Autres' : 'Other',
    icon: Globe,
    items: otherItems,
    collapsible: true
  });

  // Flatten for backward compatibility (navItems used elsewhere)
  const navItems = menuCategories.flatMap(cat => cat.items);

  // Statistics
  if (['NATIONAL_ADMIN', 'TECH_SUPPORT', 'MINISTER', 'PRESIDENT', 'SUPER_ADMIN', 'FINANCIAL_AUDITOR'].includes(user?.role)) {
    navItems.push({ path: '/statistics', icon: FileBarChart, label: language === 'fr' ? 'Statistiques BI' : 'BI Statistics' });
  }

  // Audit logs
  if (['NATIONAL_ADMIN', 'TECH_SUPPORT', 'FINANCIAL_AUDITOR', 'MINISTER', 'PRESIDENT', 'SUPER_ADMIN'].includes(user?.role)) {
    navItems.push({ path: '/audit-logs', icon: FileBarChart, label: t(language, 'nav.auditLogs') });
  }

  navItems.push({ path: '/settings', icon: Settings, label: t(language, 'nav.settings') });

  // Contrast color calculation
  const getContrastColor = (hexColor) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#0f172a' : '#ffffff';
  };

  const activeTextColor = getContrastColor(activeColor);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 text-white transition-all duration-300 ease-out hidden lg:block ${
          sidebarOpen ? 'w-64' : 'w-16'
        }`}
        style={{ backgroundColor: sidebarBg }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b" style={{ borderColor: `${activeColor}30` }}>
          {sidebarOpen && (
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded flex items-center justify-center"
                style={{ backgroundColor: activeColor }}
              >
                <Scale className="w-6 h-6" style={{ color: activeTextColor }} />
              </div>
              <div>
                <h1 className="font-heading font-bold text-sm tracking-tight">SNIGJ</h1>
                <p className="text-[10px] opacity-60 tracking-wide">RDC</p>
              </div>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded transition-colors hover:bg-white/10"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* User Profile Card - MOVED TO TOP (before menu) */}
        {sidebarOpen && user && (
          <div className="p-3 border-b" style={{ borderColor: `${activeColor}30` }}>
            <div className="flex items-center gap-3 p-2 rounded-lg bg-white/5">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-10 h-10 rounded-full object-cover border-2 flex-shrink-0"
                  style={{ borderColor: activeColor }}
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: activeColor, color: activeTextColor }}
                >
                  {user.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-[10px] text-white/60 truncate">{t(language, `roles.${user.role}`)}</p>
              </div>
              <span 
                className={`px-1.5 py-0.5 rounded text-[8px] font-medium uppercase tracking-wider flex-shrink-0 classification-${user.clearance_level?.toLowerCase()}`}
              >
                {user.clearance_level?.substring(0, 3)}
              </span>
            </div>
          </div>
        )}

        {/* Navigation with Categories */}
        <nav className="p-2 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)' }}>
          {menuCategories.map((category) => {
            const isExpanded = expandedCategories[category.id] !== false;
            const hasActiveItem = category.items.some(item => 
              location.pathname === item.path || 
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
            );

            return (
              <div key={category.id} className="space-y-0.5">
                {/* Category Header */}
                {category.label && sidebarOpen && (
                  <button
                    onClick={() => category.collapsible && toggleCategory(category.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider transition-colors ${
                      category.collapsible ? 'cursor-pointer hover:bg-white/5' : 'cursor-default'
                    }`}
                    style={{ color: hasActiveItem ? activeColor : 'rgba(255,255,255,0.5)' }}
                  >
                    <div className="flex items-center gap-2">
                      {category.icon && <category.icon className="w-4 h-4" />}
                      <span>{category.label}</span>
                    </div>
                    {category.collapsible && (
                      isExpanded 
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Category Items */}
                {(!category.collapsible || isExpanded || !sidebarOpen) && (
                  <div className={sidebarOpen && category.label ? 'pl-2' : ''}>
                    {category.items.map((item) => {
                      const isActive = location.pathname === item.path || 
                                      (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-${item.path.replace(/\//g, '-').substring(1)}`}
                          className="flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-all duration-150"
                          style={{
                            backgroundColor: isActive ? activeColor : 'transparent',
                            color: isActive ? activeTextColor : 'rgba(255,255,255,0.7)'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = `${activeColor}20`;
                              e.currentTarget.style.color = '#ffffff';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                            }
                          }}
                        >
                          <item.icon className="w-5 h-5 flex-shrink-0" />
                          {sidebarOpen && <span className="truncate">{item.label}</span>}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Sidebar - Mobile */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-72 text-white transition-transform duration-300 ease-out lg:hidden ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: sidebarBg }}
      >
        {/* Mobile Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b" style={{ borderColor: `${activeColor}30` }}>
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded flex items-center justify-center"
              style={{ backgroundColor: activeColor }}
            >
              <Scale className="w-6 h-6" style={{ color: activeTextColor }} />
            </div>
            <div>
              <h1 className="font-heading font-bold text-sm tracking-tight">SNIGJ</h1>
              <p className="text-[10px] opacity-60 tracking-wide">RDC</p>
            </div>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded transition-colors hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile User Profile */}
        {user && (
          <div className="p-3 border-b" style={{ borderColor: `${activeColor}30` }}>
            <div className="flex items-center gap-3 p-2 rounded-lg bg-white/5">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-10 h-10 rounded-full object-cover border-2 flex-shrink-0"
                  style={{ borderColor: activeColor }}
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: activeColor, color: activeTextColor }}
                >
                  {user.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-[10px] text-white/60 truncate">{t(language, `roles.${user.role}`)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Navigation with Categories */}
        <nav className="p-2 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)' }}>
          {menuCategories.map((category) => {
            const isExpanded = expandedCategories[category.id] !== false;
            const hasActiveItem = category.items.some(item => 
              location.pathname === item.path || 
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
            );

            return (
              <div key={category.id} className="space-y-0.5">
                {/* Category Header */}
                {category.label && (
                  <button
                    onClick={() => category.collapsible && toggleCategory(category.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider transition-colors ${
                      category.collapsible ? 'cursor-pointer hover:bg-white/5' : 'cursor-default'
                    }`}
                    style={{ color: hasActiveItem ? activeColor : 'rgba(255,255,255,0.5)' }}
                  >
                    <div className="flex items-center gap-2">
                      {category.icon && <category.icon className="w-4 h-4" />}
                      <span>{category.label}</span>
                    </div>
                    {category.collapsible && (
                      isExpanded 
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Category Items */}
                {(!category.collapsible || isExpanded) && (
                  <div className={category.label ? 'pl-2' : ''}>
                    {category.items.map((item) => {
                      const isActive = location.pathname === item.path || 
                                      (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all"
                          style={{
                            backgroundColor: isActive ? activeColor : 'transparent',
                            color: isActive ? activeTextColor : 'rgba(255,255,255,0.7)'
                          }}
                        >
                          <item.icon className="w-5 h-5 flex-shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'}`}>
        {/* Header */}
        <header className="h-14 sm:h-16 bg-card border-b flex items-center justify-between px-3 sm:px-6 sticky top-0 z-40">
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded lg:hidden hover:bg-muted"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Search - Hidden on mobile */}
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={t(language, 'common.search') + '...'}
                className="pl-10 pr-4 py-2 w-48 md:w-64 rounded-sm border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                data-testid="global-search"
              />
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-3">
            {/* Language Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 sm:gap-2 px-2 sm:px-3" data-testid="language-toggle">
                  <Globe className="w-4 h-4" />
                  <span className="text-xs font-medium uppercase hidden sm:inline">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => updateLanguage('fr')}>🇫🇷 Français</DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateLanguage('en')}>🇬🇧 English</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => updateLanguage('ln')}>🇨🇩 Lingála</DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateLanguage('sw')}>🇨🇩 Kiswahili</DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateLanguage('kg')}>🇨🇩 Kikongo</DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateLanguage('lu')}>🇨🇩 Tshiluba</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme Toggle */}
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="w-8 h-8 sm:w-9 sm:h-9" data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="w-4 h-4 sm:w-5 sm:h-5" /> : <Moon className="w-4 h-4 sm:w-5 sm:h-5" />}
            </Button>

            {/* Notifications - Real-time polling */}
            <NotificationCenter />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-1 sm:gap-2 px-2 sm:px-3" data-testid="user-menu">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full" />
                  ) : (
                    <UserCircle className="w-7 h-7 sm:w-8 sm:h-8" />
                  )}
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium text-sm">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/my-profile')} data-testid="my-profile-link">
                  <UserCircle className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Mon Profil' : 'My Profile'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  {t(language, 'nav.settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t(language, 'nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content - Responsive padding */}
        <main className="p-3 sm:p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
